import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingIndicator } from 'atoms';
import { usePandaDocAnalytics } from 'core/hooks';

import * as S from '../PandaDocAnalytics.styled';
import { COLORS, Granularity } from '../constants';
import { KpiCard, ChartCard } from '../components';
import { ConversionNote } from '../ConversionNote';
import {
  BarChart,
  HistogramChart,
  LineChart,
  StackedBarChart,
} from '../charts';
import { countDefinedList, fmtNum, fmtPct, fmtTTS, sumList } from '../utils';

/** The three document types that get their own tab. */
export type DocTypeKey = 'students' | 'trainers' | 'dofin';

const TYPE_META: Record<
  DocTypeKey,
  { titleKey: string; color: string; hasConversion: boolean; apiType: string }
> = {
  students: {
    titleKey: 'pages.pandadocAnalytics.type.studentsTitle',
    color: COLORS.students,
    hasConversion: true,
    apiType: 'student',
  },
  dofin: {
    titleKey: 'pages.pandadocAnalytics.type.dofinTitle',
    color: COLORS.dofin,
    hasConversion: true,
    apiType: 'dofin',
  },
  trainers: {
    titleKey: 'pages.pandadocAnalytics.type.trainersTitle',
    color: COLORS.trainers,
    hasConversion: false,
    apiType: 'trainer',
  },
};

interface ITypeTabProps {
  docType: DocTypeKey;
  granularity: Granularity;
  course: string;
  dateFrom: string | null;
  dateTo: string | null;
}

/**
 * Per-document-type tab. Runs its own analytics query with `docType` so the
 * TTS KPI and histogram are scoped to this type (the shell query is all-types).
 */
export const TypeTab = memo(
  ({ docType, granularity, course, dateFrom, dateTo }: ITypeTabProps) => {
    const { t } = useTranslation();
    const meta = TYPE_META[docType];
    const { data, loading, fetching } = usePandaDocAnalytics({
      granularity,
      course,
      docType: meta.apiType,
      dateFrom,
      dateTo,
    });

    if (loading && !data) {
      return (
        <S.CenterLoader>
          <LoadingIndicator />
        </S.CenterLoader>
      );
    }

    if (!data || (data.window?.docsLoaded ?? 0) === 0) {
      return (
        <S.EmptyState>{t('pages.pandadocAnalytics.emptyType')}</S.EmptyState>
      );
    }

    const volumeSeries = data.volumeSeries?.[docType];
    const ttsSeries = data.ttsMedianSeries?.[docType];
    // `conversionSeries` only exposes students and dofin (trainers excluded).
    const conversionSeries =
      docType === 'trainers' ? undefined : data.conversionSeries?.[docType];
    const tts = data.ttsKpi;

    const signedTotal = data.volumeKpi?.[docType];
    const inSample = sumList(volumeSeries?.values);

    const convSigned = sumList(conversionSeries?.signed);
    const convActive = sumList(conversionSeries?.active);
    const convLost = sumList(conversionSeries?.lost);
    const convSent = convSigned + convActive + convLost;
    const convRate = convSent
      ? Math.round((convSigned / convSent) * 100)
      : null;

    return (
      <S.DimWhenFetching fetching={fetching}>
        <S.SectionTitle>{t(meta.titleKey)}</S.SectionTitle>

        <S.KpiGrid columns={meta.hasConversion ? 5 : 3}>
          <KpiCard
            label={t('pages.pandadocAnalytics.type.kpiTotal')}
            value={fmtNum(signedTotal)}
            subLabel={t('pages.pandadocAnalytics.type.kpiTotalSub')}
            accent={meta.color}
          />
          {meta.hasConversion && (
            <>
              <KpiCard
                label={t('pages.pandadocAnalytics.type.kpiSent')}
                value={fmtNum(convSent)}
                subLabel={t('pages.pandadocAnalytics.type.kpiSentSub')}
              />
              <KpiCard
                label={t('pages.pandadocAnalytics.type.kpiConversion')}
                value={fmtPct(convRate)}
                subLabel={t('pages.pandadocAnalytics.type.kpiConversionSub', {
                  signed: fmtNum(convSigned),
                })}
                accent={COLORS.signed}
              />
            </>
          )}
          <KpiCard
            label={t('pages.pandadocAnalytics.type.kpiMedianTts')}
            value={fmtTTS(tts?.medianHours, t)}
            subLabel={t('pages.pandadocAnalytics.type.kpiMedianTtsSub', {
              sample: fmtNum(tts?.sample),
            })}
            accent={COLORS.tts}
          />
          <KpiCard
            label={t('pages.pandadocAnalytics.type.kpiPct24h')}
            value={fmtPct(tts?.pct24h)}
            subLabel={t('pages.pandadocAnalytics.type.kpiPct24hSub')}
            accent={COLORS.tts}
          />
        </S.KpiGrid>

        <ChartCard
          title={t('pages.pandadocAnalytics.type.volumeTitle')}
          dotColor={meta.color}
          meta={t('pages.pandadocAnalytics.type.volumeMeta', {
            count: inSample,
          })}
        >
          <BarChart
            labels={volumeSeries?.labels ?? []}
            values={volumeSeries?.values ?? []}
            color={meta.color}
            granularity={granularity}
          />
        </ChartCard>

        <ChartCard
          title={t('pages.pandadocAnalytics.type.distributionTitle')}
          meta={t('pages.pandadocAnalytics.type.distributionMeta', {
            sample: fmtNum(tts?.sample),
          })}
        >
          <HistogramChart
            labels={data.ttsHistogram?.labels ?? []}
            values={data.ttsHistogram?.values ?? []}
          />
        </ChartCard>

        <ChartCard
          title={t('pages.pandadocAnalytics.type.medianTitle')}
          dotColor={meta.color}
          meta={t('pages.pandadocAnalytics.type.medianMeta', {
            count: countDefinedList(ttsSeries?.values),
          })}
        >
          <LineChart
            labels={ttsSeries?.labels ?? []}
            values={ttsSeries?.values ?? []}
            color={meta.color}
            granularity={granularity}
          />
        </ChartCard>

        {meta.hasConversion && conversionSeries && <ConversionNote />}

        {meta.hasConversion && conversionSeries && (
          <ChartCard
            title={t('pages.pandadocAnalytics.type.conversionTitle')}
            dotColor={meta.color}
            tall
            legend={
              <S.Legend>
                <span>
                  <S.Dot color={COLORS.signed} />
                  {t('pages.pandadocAnalytics.charts.signed')}
                </span>
                <span>
                  <S.Dot color={COLORS.active} />
                  {t('pages.pandadocAnalytics.charts.active')}
                </span>
                <span>
                  <S.Dot color={COLORS.lost} />
                  {t('pages.pandadocAnalytics.charts.lost')}
                </span>
              </S.Legend>
            }
            footer={t('pages.pandadocAnalytics.type.conversionFooter', {
              count: convSent,
            })}
          >
            <StackedBarChart
              labels={conversionSeries.labels ?? []}
              signed={conversionSeries.signed ?? []}
              active={conversionSeries.active ?? []}
              lost={conversionSeries.lost ?? []}
              granularity={granularity}
            />
          </ChartCard>
        )}

        {!meta.hasConversion && (
          <S.FooterNote>
            {t('pages.pandadocAnalytics.type.trainersNote')}
          </S.FooterNote>
        )}
      </S.DimWhenFetching>
    );
  },
);
